import { AppMenu, Flex, Text } from '@ghq-abi/design-system';
import { T } from '@tolgee/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useNav } from '~/app/contexts/NavContext';
import { RouteObject } from '~/app/types';
import { BackArrowIcon, OurAppsIcon } from '~/shared/components/icons';
import { HamburguerIcon } from '~/shared/components/icons/HamburgerIcon';
import { MenuSkeleton } from '../MenuSkeleton';

function getAsComponent(item: { external?: boolean; path?: string }) {
  if (item.external) return undefined;
  if (item.path) return Link;
  return undefined;
}

export function MobileMenu() {
  const router = useRouter();
  const {
    isAppMenuOpen,
    subMenuOpen,
    menuHeaderItem,
    menuItems,
    footerItems,
    isMenuLoading,
    toggleAppMenu,
    checkIfIsActive,
    toggleSubMenu,
  } = useNav();

  const onMenuItemClick = (item: RouteObject) => {
    if (item.external && item.path) {
      router.push(item.path);
      toggleSubMenu(null);
      toggleAppMenu();
    } else if (item.routeComponents?.length) {
      toggleSubMenu(item);
    } else {
      toggleSubMenu(null);
      toggleAppMenu();
    }
  };

  if (isMenuLoading) {
    return <MenuSkeleton />;
  }

  return (
    <AppMenu open={isAppMenuOpen}>
      <AppMenu.Trigger asChild>
        <AppMenu.MenuIconButton
          onClick={toggleAppMenu}
          icon={<HamburguerIcon />}
        />
      </AppMenu.Trigger>
      <AppMenu.Content
        overlayIsActive
        closeMenu={() => {
          toggleAppMenu();
          toggleSubMenu(null);
        }}
      >
        <AppMenu.Header css={{ p: 0 }}>
          {subMenuOpen ? (
            <Flex
              align="center"
              gap="sm"
              css={{ width: '$full', height: '$appHeaderHeight', p: '$2' }}
            >
              <AppMenu.MenuIconButton
                onClick={() => toggleSubMenu(null)}
                icon={<BackArrowIcon />}
              />
              <Text css={{ fontWeight: '$medium' }}>
                {subMenuOpen.title || ''}
              </Text>
            </Flex>
          ) : (
            <Flex
              align="center"
              justify="start"
              css={{ width: '$full', height: '$appHeaderHeight', p: '$2', marginLeft: '8px' }}
            >
              <AppMenu.MenuIconButton
                onClick={() => toggleSubMenu(menuHeaderItem)}
                icon={<OurAppsIcon />}
              />
              <Text className='ml-[11px]'><T keyName="menu.ourApps">Our Apps</T></Text>
            </Flex>
          )}
        </AppMenu.Header>
        {subMenuOpen ? (
          <AppMenu.MenuList>
            {subMenuOpen?.routeComponents?.map(subitem => (
              <AppMenu.MenuItem
                key={subitem.key}
                as={getAsComponent(subitem)}
                title={subitem.title}
                route={subitem.external ? undefined : subitem.path}
                leftIcon={subitem.icon}
                onClick={() => onMenuItemClick(subitem)}
                isSubMenuItem
              />
            ))}
          </AppMenu.MenuList>
        ) : (
          <AppMenu.MenuList>
            {menuItems.map(item => (
              <AppMenu.MenuItem
                key={item.key}
                as={getAsComponent(item)}
                route={item.external ? undefined : item.path}
                title={item.title}
                leftIcon={item.icon}
                hasSubmenu={!!item.routeComponents?.length}
                isActive={item.path && !item.external ? checkIfIsActive(item.path, item.routeComponents) : false}
                onClick={() => onMenuItemClick(item)}
              />
            ))}
          </AppMenu.MenuList>
        )}
        <AppMenu.Footer>
          {footerItems.map(item => (
            <AppMenu.MenuItem
              key={item.key}
              as={getAsComponent(item)}
              title={item.title}
              route={item.external ? undefined : item.path}
              hasSubmenu={!!item?.routeComponents?.length}
              leftIcon={item.icon}
              isActive={item.path && !item.external ? checkIfIsActive(item.path, item.routeComponents) : false}
              onClick={() => onMenuItemClick(item)}
            />
          ))}
        </AppMenu.Footer>
      </AppMenu.Content>
    </AppMenu>
  );
}
